import React, { useState, useMemo } from 'react';
import { Modal, Form, InputGroup, Button, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FixedSizeList as List } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import { usePesquisarProdutosInfinito } from '../../../hooks/useSngpc';
import { ProdutoParaInventarioType } from '../../../data/sngpc';
import LinkProduto from '../../../components/common/LinkProduto';

interface ModalPesquisarProdutosProps {
    show: boolean;
    onHide: () => void;
    onSelecionarProduto: (produto: ProdutoParaInventarioType) => void;
}

interface PageResult {
    isSuccess: boolean;
    data: ProdutoParaInventarioType[];
}

const ModalPesquisarProdutos: React.FC<ModalPesquisarProdutosProps> = ({
    show,
    onHide,
    onSelecionarProduto
}) => {
    const [termoPesquisa, setTermoPesquisa] = useState('');

    // Query de pesquisa (só executa quando modal está aberto e há termo de pesquisa)
    const produtosQuery = usePesquisarProdutosInfinito(show && termoPesquisa ? termoPesquisa : undefined);

    // Preparar dados para rolagem infinita
    const allProdutos = useMemo(() => {
        if (!produtosQuery.data) return [];
        return produtosQuery.data.pages.flatMap((page: PageResult) =>
            page.isSuccess ? page.data : []
        );
    }, [produtosQuery.data]);

    // Verificar se há mais itens para carregar
    const hasNextPage = produtosQuery.hasNextPage;
    const isLoadingMore = produtosQuery.isFetchingNextPage;

    const handlePesquisar = () => {
        // A query será refeita automaticamente devido ao useInfiniteQuery
        produtosQuery.refetch();
    };

    const handleSelecionarProduto = (produtoId: number) => {
        const produto = allProdutos.find((p: ProdutoParaInventarioType) => p.id === produtoId);
        if (produto) {
            onSelecionarProduto(produto);
        }
    };

    // Função para verificar se um item está carregado
    const isItemLoaded = (index: number) => !!allProdutos[index];

    // Função para carregar mais itens
    const loadMoreItems = () => {
        if (hasNextPage && !isLoadingMore) {
            produtosQuery.fetchNextPage();
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FontAwesomeIcon icon={faSearch} className="me-2 text-primary" />
                    Pesquisar e Incluir Produtos
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Barra de pesquisa */}
                <Row className="mb-4">
                    <Col>
                        <InputGroup>
                            <Form.Control
                                type="text"
                                placeholder="Pesquisar por nome, código de barras ou fabricante..."
                                value={termoPesquisa}
                                onChange={(e) => setTermoPesquisa(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handlePesquisar()}
                            />
                            <Button variant="outline-secondary" onClick={handlePesquisar}>
                                <FontAwesomeIcon icon={faSearch} />
                            </Button>
                            {termoPesquisa && (
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => setTermoPesquisa('')}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </Button>
                            )}
                        </InputGroup>
                    </Col>
                </Row>

                {/* Lista de produtos com rolagem infinita */}
                {termoPesquisa ? (
                    <div>
                        {/* Header da tabela */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <strong>{allProdutos.length}</strong> produto(s) carregado(s)
                            </div>
                        </div>

                        {/* Lista virtualizada */}
                        <InfiniteLoader
                            isItemLoaded={isItemLoaded}
                            itemCount={hasNextPage ? allProdutos.length + 1 : allProdutos.length}
                            loadMoreItems={loadMoreItems}
                        >
                            {({ onItemsRendered, ref }) => (
                                <List
                                    ref={ref}
                                    height={400}
                                    itemCount={hasNextPage ? allProdutos.length + 1 : allProdutos.length}
                                    itemSize={80}
                                    onItemsRendered={onItemsRendered}
                                    width="100%"
                                    itemData={{
                                        produtos: allProdutos,
                                        handleSelecionarProduto,
                                        isLoadingMore,
                                        hasNextPage
                                    }}
                                >
                                    {ProdutoRow}
                                </List>
                            )}
                        </InfiniteLoader>
                    </div>
                ) : (
                    <div className="text-center py-5">
                        <FontAwesomeIcon icon={faSearch} size="3x" className="text-muted mb-3" />
                        <h5 className="text-muted mb-3">Pesquisar Produtos</h5>
                        <p className="text-muted">
                            Digite o nome, código de barras ou fabricante do produto que deseja incluir no inventário.
                        </p>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Fechar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// Componente para renderizar cada produto na lista
const ProdutoRow = ({ index, style, data }: any) => {
    const { produtos, handleSelecionarProduto, isLoadingMore, hasNextPage } = data;

    // Se estamos no final da lista e ainda há mais páginas, mostrar loading
    if (index >= produtos.length) {
        return (
            <div style={style} className="d-flex align-items-center justify-content-center">
                {isLoadingMore ? (
                    <div className="text-muted">
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Carregando...</span>
                        </div>
                        Carregando mais produtos...
                    </div>
                ) : hasNextPage ? (
                    <div className="text-muted">Carregando...</div>
                ) : null}
            </div>
        );
    }

    const produto = produtos[index];

    return (
        <div style={style} className="border-bottom p-2">
            <div className="d-flex align-items-center">
                <div className="me-3">
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleSelecionarProduto(produto.id)}
                    >
                        {produto.jaNoInventario ? "Adicionar Lote" : "Incluir"}
                    </Button>
                </div>
                <div className="flex-grow-1">
                    <div>
                        <LinkProduto produtoId={produto.id}>
                            <strong>{produto.nome}</strong>
                        </LinkProduto>
                        <div className="text-muted small">
                            Código: {produto.codigo}
                            {produto.codigoBarras && ` | EAN: ${produto.codigoBarras}`}
                        </div>
                        <div className="text-muted small">
                            {produto.fabricante || 'Sem fabricante'}
                        </div>
                    </div>
                </div>
                <div className="text-end">
                    <div className={produto.estoqueAtual > 0 ? 'text-success' : 'text-muted'}>
                        {produto.estoqueAtual} {produto.unidade || 'UN'}
                    </div>
                    <div className="d-flex gap-1 justify-content-end mt-1">
                        {produto.ehControlado && (
                            <span className="badge bg-warning text-dark">Controlado</span>
                        )}
                        {produto.jaNoInventario ? (
                            <span className="badge bg-info">Já no Inventário</span>
                        ) : (
                            <span className="badge bg-secondary">Disponível</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalPesquisarProdutos; 